import React, { ChangeEvent, useCallback, useMemo, useState } from "react";
import {
  Col,
  Container,
  FormGroup,
  Input,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import { TriangleDownIcon, TriangleUpIcon } from "@primer/octicons-react";

import useApi from "~/hooks/use-api";

type Warnings = Record<
  string,
  Array<{ id: string; title: string; description: string }>
>;

interface Organization {
  uid: string;
  name: string;
  timezone: string;
  currency: string;
  disabled: string;
  accountManager: string | null;
}

export default function WarningsPage() {
  const { data: warnings, isValidating } = useApi<Warnings>(`/api/warnings`);
  const { data: orgs } = useApi<Organization[]>(
    "/api/organization?includeDisabled=true"
  );
  const [sortBy, setSortBy] = useState<{ field: string; dir: 1 | -1 }>({
    field: "warnings",
    dir: 1,
  });
  const [filterBy, setFilterBy] = useState<{
    org?: string;
    accountManager?: string;
    warning?: string;
  }>({});

  const handleSort = useCallback((sortField: string) => {
    setSortBy((cur) => {
      console.log(cur);
      return {
        field: sortField,
        dir: cur.field === sortField ? (-cur.dir as 1 | -1) : 1,
      };
    });
  }, []);

  const handleFilterSelect = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setFilterBy((cur) => {
        console.log(cur);
        return {
          ...cur,
          [event.target.name]: event.target.value || undefined,
        };
      });
    },
    []
  );

  const orgWarnings = useMemo(() => {
    return (
      warnings &&
      Object.entries(warnings).flatMap(([orgId, orgWarnings]) => {
        const org = orgs?.find((o) => o.uid === orgId);
        if (!org) {
          return [];
        }

        return orgWarnings.map((warning) => ({ org, warning }));
      })
    );
  }, [warnings, orgs]);

  const sortedWarnings = useMemo(() => {
    return orgWarnings
      ?.filter((w) => {
        const { org, warning } = w;
        if (filterBy.org && filterBy.org !== org.uid) {
          return false;
        }
        if (
          filterBy.accountManager &&
          filterBy.accountManager !== org.accountManager
        ) {
          return false;
        }
        if (filterBy.warning && filterBy.warning !== warning.title) {
          return false;
        }
        return true;
      })
      .sort((a, b) => {
        switch (sortBy.field) {
          case "orgs":
            return sortBy.dir * a.org.name.localeCompare(b.org.name);
          case "account_manager":
            return (
              sortBy.dir *
              (a.org.accountManager || "")?.localeCompare(
                b.org.accountManager || ""
              )
            );
          default:
            return sortBy.dir * a.warning.title.localeCompare(b.warning.title);
        }
      });
  }, [orgWarnings, sortBy, filterBy]);

  const accountManagers = useMemo(
    () => (orgs ? sortedUniq(orgs.map((o) => o.accountManager)) : []),
    [orgs]
  );

  const uniqWarnings = useMemo(
    () =>
      orgWarnings ? sortedUniq(orgWarnings.map((w) => w.warning.title)) : [],
    [orgWarnings]
  );

  if (!sortedWarnings) {
    if (isValidating) {
      return <Spinner />;
    }
    return <div>No warnings</div>;
  }

  return (
    <Container>
      <Row className="mt-4">
        <Col>
          <FormGroup>
            <Input
              type="select"
              name="org"
              onChange={handleFilterSelect}
              value={filterBy.org}
            >
              <option value="">All Orgs</option>
              {orgs?.map((org) => (
                <option key={org.uid} value={org.uid}>
                  {org.name}
                </option>
              ))}
            </Input>
          </FormGroup>
        </Col>
        <Col>
          <FormGroup>
            <Input
              type="select"
              name="accountManager"
              onChange={handleFilterSelect}
              value={filterBy.accountManager}
            >
              <option value="">All Account Managers</option>
              {accountManagers.map((email) => (
                <option key={email} value={email}>
                  {email}
                </option>
              ))}
            </Input>
          </FormGroup>
        </Col>
        <Col>
          <FormGroup>
            <Input
              type="select"
              name="warning"
              onChange={handleFilterSelect}
              value={filterBy.warning}
            >
              <option value="">All Warnings</option>
              {uniqWarnings.map((w) => (
                <option key={w} value={w}>
                  {w}
                </option>
              ))}
            </Input>
          </FormGroup>
        </Col>
      </Row>
      <Table responsive>
        <thead>
          <tr>
            <TableColHeader
              title="Organization"
              field="orgs"
              onSort={handleSort}
              sortBy={sortBy}
            />
            <TableColHeader
              title="Account Manager"
              field="account_manager"
              onSort={handleSort}
              sortBy={sortBy}
            />
            <TableColHeader
              title="Warning"
              field="warnings"
              onSort={handleSort}
              sortBy={sortBy}
            />
          </tr>
        </thead>
        <tbody>
          {sortedWarnings.map(({ org, warning }) => {
            return (
              <tr key={org.uid + warning.id}>
                <td>{org.name}</td>
                <td>{org.accountManager}</td>
                <td>{warning.title}</td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </Container>
  );
}

function TableColHeader(p: {
  title: string;
  field: string;
  onSort: (field: string) => void;
  sortBy: { field: string; dir: 1 | -1 };
}) {
  let icon = null;
  if (p.sortBy.field === p.field) {
    icon = p.sortBy.dir === 1 ? <TriangleDownIcon /> : <TriangleUpIcon />;
  }

  return (
    <th>
      <span
        style={{ cursor: "pointer", userSelect: "none" }}
        onClick={() => p.onSort(p.field)}
      >
        {p.title}
      </span>{" "}
      {icon}
    </th>
  );
}

function sortedUniq(arr: (string | undefined | null)[]): string[] {
  return Array.from(
    new Set(arr.filter<string>((v): v is string => Boolean(v)))
  ).sort();
}
